import { useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import {
  getFoldersApi,
  getFoldersReset,
  createFolderApi,
  createFolderReset,
  renameFolderApi,
  renameFolderReset,
  deleteFolderApi,
  deleteFolderReset,
  addItemToFolderApi,
  addItemToFolderReset,
  removeItemFromFolderApi,
  removeItemFromFolderReset,
} from '../store/actions/folderAction';
import { ThemeForm } from '../pages/Styles/Theme';

/**
 * Shared hook for folder functionality across all listing pages.
 *
 * @param {Object} options
 * @param {string} options.entityType - 'project' or 'polling_agent'
 * @param {string} options.searchValue - current search input for client-side folder filtering
 * @returns {Object} All folder state, handlers, computed values, and forms
 */
const useFolders = ({ entityType = 'project', searchValue = '' }) => {
  const dispatch = useDispatch();

  // Redux state
  const getFoldersState = useSelector((state) => state.folder.getFoldersState);
  const createFolderState = useSelector((state) => state.folder.createFolderState);
  const renameFolderState = useSelector((state) => state.folder.renameFolderState);
  const deleteFolderState = useSelector((state) => state.folder.deleteFolderState);
  const addItemToFolderState = useSelector((state) => state.folder.addItemToFolderState);
  const removeItemFromFolderState = useSelector((state) => state.folder.removeItemFromFolderState);

  // Form instances
  const [folderForm] = ThemeForm.useForm();
  const [renameFolderForm] = ThemeForm.useForm();

  // Folder state
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('folderId') || null;
  });
  const [currentFolderName, setCurrentFolderName] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('folderName') || '';
  });

  // Modal state
  const [createFolderModal, setCreateFolderModal] = useState(false);
  const [renameFolderModal, setRenameFolderModal] = useState(false);
  const [folderToRename, setFolderToRename] = useState(null);
  const [deleteFolderModal, setDeleteFolderModal] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [moveToFolderModal, setMoveToFolderModal] = useState(false);
  const [itemToMove, setItemToMove] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [createItemFolderId, setCreateItemFolderId] = useState(null);
  const [removeFromFolderModal, setRemoveFromFolderModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);

  // UI state
  const [foldersCollapsed, setFoldersCollapsed] = useState(false);
  const [itemsCollapsed, setItemsCollapsed] = useState(false);

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const draggedItemRef = useRef(null);

  // Counter to signal consumer that a refresh is needed
  const [refreshNeeded, setRefreshNeeded] = useState(0);
  const triggerRefresh = useCallback(() => {
    setRefreshNeeded((prev) => prev + 1);
  }, []);

  // Client-side folder filtering
  const filteredFolders = searchValue
    ? folders.filter((folder) => folder.name.toLowerCase().includes(searchValue.toLowerCase()))
    : folders;

  // ==================== Redux dispatch wrappers ====================

  const fetchFolders = useCallback(() => {
    dispatch(getFoldersApi(entityType));
  }, [dispatch, entityType]);

  const dispatchCreateFolder = useCallback((data) => {
    dispatch(createFolderApi({ ...data, entity_type: entityType }));
  }, [dispatch, entityType]);

  const dispatchRenameFolder = useCallback((id, data) => {
    dispatch(renameFolderApi(id, data));
  }, [dispatch]);

  const dispatchDeleteFolder = useCallback((id) => {
    dispatch(deleteFolderApi(id));
  }, [dispatch]);

  const dispatchAddItemToFolder = useCallback((folderId, itemId) => {
    dispatch(addItemToFolderApi(folderId, itemId));
  }, [dispatch]);

  const dispatchRemoveItemFromFolder = useCallback((folderId, itemId) => {
    dispatch(removeItemFromFolderApi(folderId, itemId));
  }, [dispatch]);

  // ==================== Response handlers (called by consumer useEffects) ====================
  // These are designed to be called from useEffects in the consumer,
  // watching the corresponding Redux state.

  const handleGetFoldersResponse = useCallback(() => {
    if (getFoldersState.apiState === 'success' && getFoldersState.data) {
      const foldersData = getFoldersState.data.data || getFoldersState.data;
      setFolders(foldersData.folders || []);
      dispatch(getFoldersReset());
    } else if (getFoldersState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getFoldersState.message || 'Failed to fetch folders',
      });
      dispatch(getFoldersReset());
    }
  }, [getFoldersState, dispatch]);

  const handleCreateFolderResponse = useCallback(() => {
    if (createFolderState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: 'Folder created successfully',
      });
      setCreateFolderModal(false);
      folderForm.resetFields();
      fetchFolders();
      dispatch(createFolderReset());
    } else if (createFolderState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: createFolderState.message || 'Failed to create folder',
      });
      dispatch(createFolderReset());
    }
  }, [createFolderState, dispatch, fetchFolders, folderForm]);

  const handleRenameFolderResponse = useCallback(() => {
    if (renameFolderState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: 'Folder renamed successfully',
      });
      setRenameFolderModal(false);
      if (folderToRename && folderToRename._id === currentFolderId) {
        const renamedData = renameFolderState.data?.data || renameFolderState.data;
        if (renamedData?.name) setCurrentFolderName(renamedData.name);
      }
      setFolderToRename(null);
      renameFolderForm.resetFields();
      fetchFolders();
      dispatch(renameFolderReset());
    } else if (renameFolderState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: renameFolderState.message || 'Failed to rename folder',
      });
      dispatch(renameFolderReset());
    }
  }, [renameFolderState, dispatch, fetchFolders, renameFolderForm, folderToRename, currentFolderId]);

  const handleDeleteFolderResponse = useCallback(() => {
    if (deleteFolderState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: 'Folder deleted successfully',
      });
      setDeleteFolderModal(false);
      if (folderToDelete && folderToDelete._id === currentFolderId) {
        setCurrentFolderId(null);
        setCurrentFolderName('');
      }
      setFolderToDelete(null);
      fetchFolders();
      triggerRefresh();
      dispatch(deleteFolderReset());
    } else if (deleteFolderState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: deleteFolderState.message || 'Failed to delete folder',
      });
      dispatch(deleteFolderReset());
    }
  }, [deleteFolderState, dispatch, fetchFolders, folderToDelete, currentFolderId, triggerRefresh]);

  const handleAddItemToFolderResponse = useCallback(() => {
    if (addItemToFolderState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: 'Moved to folder',
      });
      setMoveToFolderModal(false);
      setItemToMove(null);
      setSelectedFolderId(null);
      fetchFolders();
      triggerRefresh();
      dispatch(addItemToFolderReset());
    } else if (addItemToFolderState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: addItemToFolderState.message || 'Failed to move item',
      });
      dispatch(addItemToFolderReset());
    }
  }, [addItemToFolderState, dispatch, fetchFolders, triggerRefresh]);

  const handleRemoveItemFromFolderResponse = useCallback(() => {
    if (removeItemFromFolderState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description: 'Removed from folder',
      });
      setRemoveFromFolderModal(false);
      setItemToRemove(null);
      fetchFolders();
      triggerRefresh();
      dispatch(removeItemFromFolderReset());
    } else if (removeItemFromFolderState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: removeItemFromFolderState.message || 'Failed to remove from folder',
      });
      dispatch(removeItemFromFolderReset());
    }
  }, [removeItemFromFolderState, dispatch, fetchFolders, triggerRefresh]);

  // ==================== Folder action handlers ====================

  const handleCreateFolder = useCallback(() => {
    folderForm.validateFields().then((values) => {
      const duplicate = folders.find(
        (f) => f.name.toLowerCase() === values.folderName.trim().toLowerCase()
      );
      if (duplicate) {
        notification.warning({
          message: 'Duplicate Name',
          description: 'A folder with this name already exists',
        });
        return;
      }
      dispatchCreateFolder({ name: values.folderName });
    });
  }, [folderForm, folders, dispatchCreateFolder]);

  const handleRenameFolder = useCallback(() => {
    renameFolderForm.validateFields().then((values) => {
      const duplicate = folders.find(
        (f) => f._id !== folderToRename._id && f.name.toLowerCase() === values.folderName.trim().toLowerCase()
      );
      if (duplicate) {
        notification.warning({
          message: 'Duplicate Name',
          description: 'A folder with this name already exists',
        });
        return;
      }
      dispatchRenameFolder(folderToRename._id, { name: values.folderName });
    });
  }, [renameFolderForm, folders, folderToRename, dispatchRenameFolder]);

  const handleDeleteFolder = useCallback(() => {
    if (folderToDelete) {
      dispatchDeleteFolder(folderToDelete._id);
    }
  }, [folderToDelete, dispatchDeleteFolder]);

  const handleMoveToFolder = useCallback(() => {
    if (itemToMove && selectedFolderId) {
      if (selectedFolderId === '__root__') {
        // Move to root = remove from current folder
        dispatchRemoveItemFromFolder(currentFolderId, itemToMove.id);
        setMoveToFolderModal(false);
        setItemToMove(null);
        setSelectedFolderId(null);
      } else {
        dispatchAddItemToFolder(selectedFolderId, itemToMove.id);
      }
    }
  }, [itemToMove, selectedFolderId, currentFolderId, dispatchAddItemToFolder, dispatchRemoveItemFromFolder]);

  const handleRemoveFromFolder = useCallback((item) => {
    setItemToRemove(item);
    setRemoveFromFolderModal(true);
  }, []);

  const confirmRemoveFromFolder = useCallback(() => {
    if (itemToRemove && currentFolderId) {
      dispatchRemoveItemFromFolder(currentFolderId, itemToRemove.id);
      setRemoveFromFolderModal(false);
      setItemToRemove(null);
    }
  }, [itemToRemove, currentFolderId, dispatchRemoveItemFromFolder]);

  // ==================== Navigation handlers ====================

  const handleFolderClick = useCallback((folder) => {
    setCurrentFolderId(folder._id);
    setCurrentFolderName(folder.name);
    // Persist folder state in URL
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('folderId', folder._id);
    queryParams.set('folderName', folder.name);
    queryParams.set('page', '1');
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  }, []);

  const handleBackToRoot = useCallback(() => {
    setCurrentFolderId(null);
    setCurrentFolderName('');
    // Remove folder state from URL
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.delete('folderId');
    queryParams.delete('folderName');
    queryParams.set('page', '1');
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  }, []);

  // ==================== Modal openers ====================

  const openRenameFolderModal = useCallback((folder) => {
    setFolderToRename(folder);
    renameFolderForm.setFieldsValue({ folderName: folder.name });
    setRenameFolderModal(true);
  }, [renameFolderForm]);

  const openDeleteFolderModal = useCallback((folder) => {
    setFolderToDelete(folder);
    setDeleteFolderModal(true);
  }, []);

  const openMoveToFolderModal = useCallback((item) => {
    setItemToMove(item);
    setSelectedFolderId(null);
    setMoveToFolderModal(true);
  }, []);

  // ==================== Drag and drop handlers (root level, card view only) ====================

  const handleDragStart = useCallback((e, item) => {
    setDraggedItem(item);
    draggedItemRef.current = item;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/crowdvox-item', item.id);
    // Create a small custom drag preview card
    const dragPreview = document.createElement('div');
    dragPreview.style.cssText = 'position:fixed;top:-1000px;left:-1000px;padding:12px 20px;background:#fff;border:2px solid #3D2969;border-radius:10px;font-family:Inter,sans-serif;font-size:14px;font-weight:600;color:#0a1b39;box-shadow:0 4px 16px rgba(0,0,0,0.18);max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;z-index:9999;';
    dragPreview.textContent = item.name;
    document.body.appendChild(dragPreview);
    e.dataTransfer.setDragImage(dragPreview, 100, 20);
    // Clean up preview element and fade original card
    setTimeout(() => {
      document.body.removeChild(dragPreview);
      e.target.closest('.ant-col').style.opacity = '0.4';
    }, 0);
  }, []);

  const handleDragEnd = useCallback((e) => {
    const col = e.target.closest('.ant-col');
    if (col) col.style.opacity = '1';
    setDraggedItem(null);
    draggedItemRef.current = null;
    setDragOverFolderId(null);
  }, []);

  const handleFolderDragOver = useCallback((e) => {
    if (!draggedItemRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleFolderDragEnter = useCallback((e, folderId) => {
    if (!draggedItemRef.current) return;
    e.preventDefault();
    setDragOverFolderId(folderId);
  }, []);

  const handleFolderDragLeave = useCallback((e) => {
    if (!draggedItemRef.current) return;
    const relatedTarget = e.relatedTarget;
    const currentTarget = e.currentTarget;
    if (!currentTarget.contains(relatedTarget)) {
      setDragOverFolderId(null);
    }
  }, []);

  const handleFolderDrop = useCallback((e, folderId) => {
    e.preventDefault();
    setDragOverFolderId(null);
    if (draggedItemRef.current) {
      dispatchAddItemToFolder(folderId, draggedItemRef.current.id);
      setDraggedItem(null);
      draggedItemRef.current = null;
    }
  }, [dispatchAddItemToFolder]);

  return {
    // Folder data
    folders,
    filteredFolders,
    currentFolderId,
    currentFolderName,
    refreshNeeded,

    // Modal state
    createFolderModal,
    setCreateFolderModal,
    renameFolderModal,
    setRenameFolderModal,
    folderToRename,
    deleteFolderModal,
    setDeleteFolderModal,
    folderToDelete,
    moveToFolderModal,
    setMoveToFolderModal,
    itemToMove,
    selectedFolderId,
    setSelectedFolderId,
    createItemFolderId,
    setCreateItemFolderId,
    removeFromFolderModal,
    setRemoveFromFolderModal,
    itemToRemove,

    // UI state
    foldersCollapsed,
    setFoldersCollapsed,
    itemsCollapsed,
    setItemsCollapsed,

    // Drag state
    draggedItem,
    dragOverFolderId,

    // Form instances
    folderForm,
    renameFolderForm,

    // Redux loading states (for button loading/disabled)
    createFolderState,
    renameFolderState,
    deleteFolderState,
    addItemToFolderState,
    removeItemFromFolderState,

    // Fetch action
    fetchFolders,

    // Response handlers (to be called from useEffects)
    getFoldersState,
    handleGetFoldersResponse,
    handleCreateFolderResponse,
    handleRenameFolderResponse,
    handleDeleteFolderResponse,
    handleAddItemToFolderResponse,
    handleRemoveItemFromFolderResponse,

    // Folder action handlers
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleMoveToFolder,
    handleRemoveFromFolder,
    confirmRemoveFromFolder,

    // Navigation handlers
    handleFolderClick,
    handleBackToRoot,

    // Modal openers
    openRenameFolderModal,
    openDeleteFolderModal,
    openMoveToFolderModal,

    // Drag handlers
    handleDragStart,
    handleDragEnd,
    handleFolderDragOver,
    handleFolderDragEnter,
    handleFolderDragLeave,
    handleFolderDrop,

    // Direct dispatch for consumer use (e.g., after creating an item with folder pre-selected)
    addItemToFolder: dispatchAddItemToFolder,
    removeItemFromFolder: dispatchRemoveItemFromFolder,
  };
};

export default useFolders;
